import { useEffect, useState } from 'react';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { SitterStats as SitterStatsType, SitterStatsService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { AvailabilityModal } from '../SitterProfile/components/modals';

const EXCELLENT_ID = 3;
const MEDIOCRE_ID = 2;

interface StatStatusLike {
  id?: number;
  label?: string;
}

const StatIcon = ({ status }: { status?: StatStatusLike }): JSX.Element => {
  if (!status) return <span />;
  const excellent = status.id === EXCELLENT_ID;
  const mediocre = status.id === MEDIOCRE_ID;
  const color = excellent ? 'text-emerald' : mediocre ? 'text-amber-500' : 'text-red-600';
  return (
    <div className={`text-center ${color}`}>
      <FontAwesomeIcon
        icon={excellent ? faThumbsUp : faTriangleExclamation}
        className='text-3xl'
      />
      <div className='text-xs mt-1'>{status.label}</div>
    </div>
  );
};

export const SitterStats = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter;

  const [stats, setStats] = useState<SitterStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  const refreshStats = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const resp = await SitterStatsService.get(user.id);
      setStats((resp.data?.data ?? null) as SitterStatsType | null);
    } catch (err) {
      console.warn('[SitterStats] refresh failed', err);
    }
  };

  const onAvailabilitySaved = async (): Promise<void> => {
    await Promise.all([refreshStats(), stores.sitter.refreshSitter?.()]);
  };

  useEffect(() => {
    document.title = 'UrbanSitter - Caregiver Stats';
  }, []);

  useEffect(() => {
    const userId = user?.id;

    if (!userId || !pathId) return;

    if (Number(pathId) !== userId) {
      navigate('/', { replace: true });

      return;
    }

    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterStatsService.get(userId);
        if (cancelled) return;
        setStats((resp.data?.data ?? null) as SitterStatsType | null);
      } catch (err) {
        console.warn('[SitterStats] load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [user?.id, pathId, navigate]);

  if (loading) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('pages.sitterStats.loading')}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className='container py-8'>
        <h1 className='text-2xl font-semibold mb-2'>{t('pages.sitterStats.heading')}</h1>
        <h2 className='text-lg'>{t('pages.sitterStats.calculating')}</h2>
      </div>
    );
  }

  const bookingExcellent = stats.bookingResponse?.status?.label === 'Excellent!';
  const calendarExcellent = stats.generalAvailability?.status?.label === 'Excellent!';
  const sitterId = user?.id;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.sitterStats.heading')}</h1>
      <p className='mb-6 text-sm'>
        {t('pages.sitterStats.aimForExcellent')}{' '}
        <a
          id='sitter-stat-faq'
          href='https://support.urbansitter.com/hc/en-us/articles/115000344671'
          target='_blank'
          rel='noopener noreferrer'
          className='font-semibold underline'
        >
          {t('pages.sitterStats.faqLink')}
        </a>
      </p>

      <div className='max-w-4xl space-y-6'>
        <section id='booking-response-card' className='border border-neutral-200 rounded shadow-sm bg-white overflow-hidden'>
          <div className='bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
            <h3 className='text-lg font-semibold m-0'>
              {t('pages.sitterStats.bookingResponse.heading')}
            </h3>
          </div>
          <div className='p-5'>
          <div className='text-sm mb-3'>
            <a href='/app/bookings' id='stats-to-bookings' className='underline'>
              {stats.pendingBookings?.label}
            </a>
            {' | '}
            <a
              href='/app/bookings?filterType=expired_cancel&page=1'
              id='stats-to-expired'
              className='underline'
            >
              {t('pages.sitterStats.bookingResponse.expiredLink')}
            </a>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 items-center'>
            <div className='sm:col-span-3'>
              <div className='text-2xl font-semibold'>{stats.bookingResponse?.rate ?? 0}%</div>
              <div className='text-sm'>{t('pages.sitterStats.bookingResponse.haveResponse')}</div>
            </div>
            <div className='sm:col-span-2'>
              <StatIcon status={stats.bookingResponse?.status} />
            </div>
            <div className='sm:col-span-7 sm:border-l sm:pl-4 text-sm'>
              <h4 className='font-semibold mb-2'>
                {bookingExcellent
                  ? t('pages.sitterStats.bookingResponse.tipsExcellent')
                  : t('pages.sitterStats.bookingResponse.tipsImprove')}
              </h4>
              <ul className='list-disc pl-5 space-y-1'>
                {bookingExcellent && (
                  <li>{t('pages.sitterStats.bookingResponse.tipExpiredRespond')}</li>
                )}
                {!bookingExcellent && (
                  <>
                    <li>{t('pages.sitterStats.bookingResponse.tipDontIgnore')}</li>
                    <li>{t('pages.sitterStats.bookingResponse.tipFollowUp')}</li>
                    <li>{t('pages.sitterStats.bookingResponse.tipItsOk')}</li>
                  </>
                )}
                <li>
                  {t('pages.sitterStats.bookingResponse.tipTired')}{' '}
                  {sitterId && (
                    <a
                      id='stats-to-settings-1'
                      href={`/app/sitter/${sitterId}/settings#account_settings_communications`}
                      className='underline'
                    >
                      {t('pages.sitterStats.bookingResponse.tipTiredSettings')}
                    </a>
                  )}{' '}
                  {t('pages.sitterStats.bookingResponse.tipTiredAfter')}
                </li>
                <li>{t('pages.sitterStats.bookingResponse.tipHide')}</li>
                <li>
                  {t('pages.sitterStats.bookingResponse.tipVacation')}{' '}
                  <a href='/app/sitter-calendar' id='stats-to-calendar' className='underline'>
                    {t('pages.sitterStats.bookingResponse.tipVacationRemove')}
                  </a>{' '}
                  {t('pages.sitterStats.bookingResponse.tipVacationAnd')}{' '}
                  {sitterId && (
                    <a
                      id='stats-to-settings-2'
                      href={`/app/sitter/${sitterId}/settings#account_settings_communications`}
                      className='underline'
                    >
                      {t('pages.sitterStats.bookingResponse.tipVacationAdjust')}
                    </a>
                  )}{' '}
                  {t('pages.sitterStats.bookingResponse.tipVacationAfter')}
                </li>
                {!bookingExcellent && (
                  <li>{t('pages.sitterStats.bookingResponse.tipDownload')}</li>
                )}
              </ul>
            </div>
          </div>
          </div>
        </section>

        <section id='last-cal-update-card' className='border border-neutral-200 rounded shadow-sm bg-white overflow-hidden'>
          <div className='bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
            <h3 className='text-lg font-semibold m-0'>
              {t('pages.sitterStats.lastCalendarUpdate.heading')}
            </h3>
          </div>
          <div className='p-5'>
          <Link to='/sitter-calendar' id='stats-update-calendar' className='underline text-sm'>
            {t('pages.sitterStats.lastCalendarUpdate.updateNow')}
          </Link>
          <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mt-3 items-center'>
            <div className='sm:col-span-3 text-2xl font-semibold'>
              {stats.lastCalendarUpdate?.label}
            </div>
            <div className='sm:col-span-2'>
              <StatIcon status={stats.lastCalendarUpdate?.status} />
            </div>
            <div className='sm:col-span-7 sm:border-l sm:pl-4 text-sm'>
              <h4 className='font-semibold mb-2'>
                {stats.lastCalendarUpdate?.status?.label === 'Excellent!'
                  ? t('pages.sitterStats.lastCalendarUpdate.headingExcellent')
                  : t('pages.sitterStats.lastCalendarUpdate.headingOutOfDate')}
              </h4>
              <p>
                {t('pages.sitterStats.lastCalendarUpdate.bodyPrefix')}{' '}
                <a
                  id='calendar-stats-to-calendar'
                  href='/app/sitter-calendar'
                  className='underline'
                >
                  {t('pages.sitterStats.lastCalendarUpdate.bodyLink')}
                </a>{' '}
                {t('pages.sitterStats.lastCalendarUpdate.bodySuffix')}
              </p>
            </div>
          </div>
          </div>
        </section>

        <section id='general-availability-card' className='border border-neutral-200 rounded shadow-sm bg-white overflow-hidden'>
          <div className='bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
            <h3 className='text-lg font-semibold m-0'>
              {t('pages.sitterStats.generalAvailability.heading')}
            </h3>
          </div>
          <div className='p-5'>
          <Button
            id='stats-job-seeking-status'
            buttonType={ButtonType.tertiary}
            className='underline text-sm bg-transparent border-0 p-0'
            onClick={(): void => setAvailabilityOpen(true)}
          >
            {t('pages.sitterStats.generalAvailability.updateAvailability')}
          </Button>
          <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mt-3 items-center'>
            <div className='sm:col-span-3 text-2xl font-semibold'>
              {stats.generalAvailability?.label}
            </div>
            <div className='sm:col-span-2'>
              <StatIcon status={stats.generalAvailability?.status} />
            </div>
            <div className='sm:col-span-7 sm:border-l sm:pl-4 text-sm'>
              <h4 className='font-semibold mb-2'>
                {calendarExcellent
                  ? t('pages.sitterStats.generalAvailability.headingExcellent')
                  : t('pages.sitterStats.generalAvailability.headingImprove')}
              </h4>
              <p>{t('pages.sitterStats.generalAvailability.body')}</p>
            </div>
          </div>
          </div>
        </section>

        <section id='concerning-cancellations-card' className='border border-neutral-200 rounded shadow-sm bg-white overflow-hidden'>
          <div className='bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
            <h3 className='text-lg font-semibold m-0'>
              {stats.concerningCancellations?.status?.label === 'Poor'
                ? t('pages.sitterStats.concerningCancellations.headingProblematic')
                : t('pages.sitterStats.concerningCancellations.headingConcerning')}
            </h3>
          </div>
          <div className='p-5'>
          <a
            href='https://support.urbansitter.com/hc/en-us/articles/115000316592-Reliability-Policy'
            target='_blank'
            rel='noreferrer'
            className='underline text-sm'
          >
            {t('pages.sitterStats.concerningCancellations.whatsThis')}
          </a>
          <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mt-3 items-center'>
            <div className='sm:col-span-3'>
              <div className='text-2xl font-semibold'>
                {stats.concerningCancellations?.count ?? 0}
              </div>
              <div className='text-sm'>
                {t('pages.sitterStats.concerningCancellations.bookings')}
              </div>
            </div>
            <div className='sm:col-span-2'>
              <StatIcon status={stats.concerningCancellations?.status} />
            </div>
            <div className='sm:col-span-7 sm:border-l sm:pl-4 text-sm'>
              <p>
                {stats.concerningCancellations?.status?.id === EXCELLENT_ID
                  ? t('pages.sitterStats.concerningCancellations.bodyExcellent')
                  : t('pages.sitterStats.concerningCancellations.body')}
              </p>
              <a
                href='https://support.urbansitter.com/hc/en-us/articles/115000316592-Reliability-Policy'
                target='_blank'
                rel='noreferrer'
                className='underline'
              >
                {stats.concerningCancellations?.status?.label === 'Poor'
                  ? t('pages.sitterStats.concerningCancellations.whatIsProblematic')
                  : t('pages.sitterStats.concerningCancellations.whatIsConcerning')}
              </a>
            </div>
          </div>
          </div>
        </section>
      </div>

      {sitter && (
        <AvailabilityModal
          isOpen={availabilityOpen}
          onClose={(): void => setAvailabilityOpen(false)}
          sitter={sitter}
          canEdit
          onSaved={onAvailabilitySaved}
        />
      )}
    </div>
  );
});
